import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { deleteCookie, resetGoogleTranslate } from "../../lib/helpers/helpers.js";
import { store } from "../../App.jsx";
import { setGoogleTranslateLang } from "../../actions/content.js";


const googleLangCodes = [
	{ name: "Original Language", code: "" },
	{
		name: "Afrikaans",
		code: "af",
	},
	{
		name: "Albanian",
		code: "sq",
	},
	{
		name: "Arabic",
		code: "ar",
	},
	{
		name: "Armenian",
		code: "hy",
	},
	{
		name: "Azerbaijani",
		code: "az",
	},
	{
		name: "Basque",
		code: "eu",
	},
	{
		name: "Belarusian",
		code: "be",
	},
	{
		name: "Bulgarian",
		code: "bg",
	},
	{
		name: "Catalan",
		code: "ca",
	},
	{
		name: "Chinese (Simplified)",
		code: "zh-CN",
	},
	{
		name: "Chinese (Traditional)",
		code: "zh-TW",
	},
	{
		name: "Croatian",
		code: "hr",
	},
	{
		name: "Czech",
		code: "cs",
	},
	{
		name: "Danish",
		code: "da",
	},
	{
		name: "Dutch",
		code: "nl",
	},
	{
		name: "English",
		code: "en",
	},
	{
		name: "Estonian",
		code: "et",
	},
	{
		name: "Filipino",
		code: "tl",
	},
	{
		name: "Finnish",
		code: "fi",
	},
	{
		name: "French",
		code: "fr",
	},
	{
		name: "Galician",
		code: "gl",
	},
	{
		name: "Georgian",
		code: "ka",
	},
	{
		name: "German",
		code: "de",
	},
	{
		name: "Greek",
		code: "el",
	},
	{
		name: "Haitian Creole",
		code: "ht",
	},
	{
		name: "Hebrew",
		code: "iw",
	},
	{
		name: "Hindi",
		code: "hi",
	},
	{
		name: "Hungarian",
		code: "hu",
	},
	{
		name: "Icelandic",
		code: "is",
	},
	{
		name: "Indonesian",
		code: "id",
	},
	{
		name: "Irish",
		code: "ga",
	},
	{
		name: "Italian",
		code: "it",
	},
	{
		name: "Japanese",
		code: "ja",
	},
	{
		name: "Korean",
		code: "ko",
	},
	{
		name: "Latvian",
		code: "lv",
	},
	{
		name: "Lithuanian",
		code: "lt",
	},
	{
		name: "Macedonian",
		code: "mk",
	},
	{
		name: "Malay",
		code: "ms",
	},
	{
		name: "Maltese",
		code: "mt",
	},
	{
		name: "Norwegian",
		code: "no",
	},
	{
		name: "Persian",
		code: "fa",
	},
	{
		name: "Polish",
		code: "pl",
	},
	{
		name: "Portuguese",
		code: "pt",
	},
	{
		name: "Romanian",
		code: "ro",
	},
	{
		name: "Russian",
		code: "ru",
	},
	{
		name: "Serbian",
		code: "sr",
	},
	{
		name: "Slovak",
		code: "sk",
	},
	{
		name: "Slovenian",
		code: "sl",
	},
	{
		name: "Spanish",
		code: "es",
	},
	{
		name: "Swahili",
		code: "sw",
	},
	{
		name: "Swedish",
		code: "sv",
	},
	{
		name: "Thai",
		code: "th",
	},
	{
		name: "Turkish",
		code: "tr",
	},
	{
		name: "Ukrainian",
		code: "uk",
	},
	{
		name: "Urdu",
		code: "ur",
	},
	{
		name: "Vietnamese",
		code: "vi",
	},
	{
		name: "Welsh",
		code: "cy",
	},
	{
		name: "Yiddish",
		code: "yi",
	},
];

export const DEF_LANG_NAME = "Original Language";
export const DEF_LANG_ID = "";
const TRANSLATE_OVERLAYS_CLASS = "skiptranslate";
const TRANSLATE_URL = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";

// A filter function to match any DIV element containing DIV->SVG->CIRCLE elements
// This is intended to locate the google translate spinner which currently has this 
// hierarchy and cant be targetted reliably by classnames/ids. Clearly this is a
// little unreliable as (A) the structure may change in the future, and (B) there are
// potential false positives this could match. The first of these issues is not really
// any worse than searching by class/id as those could equally change in the future. The 
// latter problem is mitigated by only searching after the script is inserted in the DOM
// which should be almost empty unless other third party scripts are inserting elements 
// there as well (currently only the twitter script does this and is highly unlikely
// to ever match the hiearchy that this filter is looking for)
function divDivSvgCircleFilter(elem) {
	if (elem && (elem.nodeName.toUpperCase() === 'DIV')) {
		const { children } = elem;
		if ((children.length === 1) && (children[0].nodeName.toUpperCase() === 'DIV')) {
			const divChildren = children[0].children;
			if ((divChildren.length === 1) && (divChildren[0].nodeName.toUpperCase() === 'SVG')) {
				const svgChildren = divChildren[0].children;
				if ((svgChildren.length === 1) && (svgChildren[0].nodeName.toUpperCase() === 'CIRCLE')) {
					return true;
				}
			}
		}
	}
	return false;
}

// Returns all subsequent siblings of the provided element ("elem") which pass the provided
// filter function ("filter") criteria, or all next siblings if no filter function is provided.
function getNextSiblings(elem, filter) {
	const matchingSiblings = [];
	let nextElem = elem.nextSibling;
	while (nextElem) {
		// ignore non-element nodes
		if (nextElem.nodeType === Node.ELEMENT_NODE) { 
			// Add any matching nodes to the array
			if (!filter || filter(nextElem)) {
				matchingSiblings.push(nextElem);
			}
		}
		nextElem = nextElem.nextSibling;
	}
	return matchingSiblings;
}

export const mapLang = (code) => {
	return googleLangCodes.find((item) => item.code === code);
};

export const updateLang = (value) => {	
	if (!value.id || value.id === "") {
		resetGoogleTranslate();
		store.dispatch(setGoogleTranslateLang({name: DEF_LANG_NAME}))
	} 
	else {
		// get all options from google translate select element
		const googleSelectElem = document.querySelector("#google_translate_element select");
		const opts = googleSelectElem ? Array.from(googleSelectElem.options) : [];
		// find the option from our dropdown in google translate's options
		const opt = opts.find((option) => {
			if (option.value === value.id) return option;
			return 0;
		});
		// change the google translate select dropdown whatever our dropdown selection was
		if (!opt && googleSelectElem) {
			googleSelectElem.selectedIndex = -1;
			googleSelectElem.dispatchEvent(new Event("change"));
		}
		if (opt && googleSelectElem) {
			googleSelectElem.selectedIndex = opt.index;
			googleSelectElem.dispatchEvent(new Event("change"));
		}
	}
	deleteCookie("googtrans", "/", window.location.hostname);
};

const googleTranslateElementInit = () => {
	if (window.google) {
		const goog = new window.google.translate.TranslateElement(
			{	autoDisplay: false },
			"google_translate_element"
		);
		return goog;
	}
	return false;
};

const cleanup = () => {
	if (scriptElem) {
		const spinnerNodes = getNextSiblings(scriptElem, divDivSvgCircleFilter);
		spinnerNodes.forEach(e => {
			e.remove();
		});
	}
	document.querySelectorAll(`#goog-gt-tt`).forEach(e => {
		e.remove();
	});
}

let scriptElem = null;

export const GoogleTranslate = ({selectedLanguage}) => {

  const useInternalTranslation = useSelector(state => state.projectConfig.useInternalTranslation);

	useEffect(() => {
		if (useInternalTranslation) {
			resetGoogleTranslate();
			store.dispatch(setGoogleTranslateLang({name: DEF_LANG_NAME}))
		}
	}, [useInternalTranslation])

	// Cleanup any inserted third-party elements when the component unmounts
	useEffect(() => {
		return () => {
			if (scriptElem) {
				// Remove any elements inserted as a result of performing translations
				document.querySelectorAll(`.${TRANSLATE_OVERLAYS_CLASS}`).forEach(e => {
					e.remove();
				});
				// Remove any elements the script itself inserted
				cleanup();
				// Remove the script itself
				scriptElem.remove();
				scriptElem = null;
			}
		}
	}, []);

	// Enable/Disable google translate components when translation mode changes
	useEffect(() => {
		if (!scriptElem && !useInternalTranslation) {
			const googleScriptNodes = document.querySelectorAll(`[src="${TRANSLATE_URL}"]`);
			if (!googleScriptNodes || googleScriptNodes.length === 0) {
				deleteCookie("googtrans", "/", window.location.hostname);
				scriptElem = document.createElement("script");
				scriptElem.setAttribute("src", TRANSLATE_URL);
				document.body.appendChild(scriptElem);
				const googleSelectElem = document.querySelector("#google_translate_element select");
				if (!googleSelectElem) {
					window.googleTranslateElementInit = googleTranslateElementInit;
				}
				// Give the script time to do its initial DOM manipulation then remove 
				// any unwanted artefacts from the DOM that it has inserted and apply
				// the currently selected target translation language to the page
				setTimeout(() => {
					cleanup();
					updateLang(selectedLanguage);
				}, 3000);
			}
			else {
				updateLang(selectedLanguage);
			}
		}

		else if (scriptElem && useInternalTranslation) {
			// Remove any elements inserted as a result of performing translations
			document.querySelectorAll(`.${TRANSLATE_OVERLAYS_CLASS}`).forEach(e => {
				e.remove();
			});
			// Remove any elements the script itself inserted
			cleanup();
			// Remove the script itself
			scriptElem.remove();
			scriptElem = null;
		}
	}, [selectedLanguage, useInternalTranslation]);

	return <div style={{display: "none"}} id="google_translate_element" />;
};

